import { DEFAULT_LOCALE, isSupportedLocale, type AuthenticatedUser } from '@fleetcore/shared'
import { create } from 'zustand'

import { applyUserLocale } from '../i18n'
import { forgetPlatformSession } from './organisation-switch'
import { api, refreshAccessToken, type TokenPair } from '../lib/api-client'
import { isApiError } from '../lib/api-error'
import { endpoints } from '../lib/endpoints'
import {
  clearTokens,
  decodeAccessTokenClaims,
  getAccessToken,
  getRefreshToken,
  notifySessionEnded,
  onSessionEnded,
  setTokens,
} from './token-storage'

export interface Credentials {
  email: string
  password: string
  /**
   * Only needed when the same address exists in more than one tenant. E-mails are
   * unique per tenant, not globally, so that is a legitimate state — and the API
   * answers it with the ordinary invalid-credentials error rather than naming the
   * tenants an address belongs to, which would disclose one customer's membership
   * to another. The organisation field is how the user breaks the tie themselves.
   */
  tenantSlug?: string
}

interface LoginResponse extends TokenPair {
  user: AuthenticatedUser
}

/**
 * `restoring` is a distinct state from `anonymous` on purpose: a reload has a
 * refresh round-trip before it knows who the user is, and rendering the login
 * page during that window would bounce an authenticated user out of the page
 * they deep-linked to.
 */
export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous'

export interface AuthState {
  status: AuthStatus
  user: AuthenticatedUser | null
  /** i18n key, never server prose — ApiErrorBody.message is developer English. */
  errorKey: string | null
  isSubmitting: boolean
  login: (credentials: Credentials) => Promise<boolean>
  logout: () => Promise<void>
  bootstrap: () => Promise<void>
  clearError: () => void
}

function loginErrorKey(error: unknown): string {
  if (!isApiError(error)) return 'auth:errors.unexpected'
  switch (error.status) {
    case 0:
      return 'auth:errors.network'
    case 400:
    case 401:
      // 400 and 401 collapse deliberately: telling the user which of the two
      // fields was wrong is an account-enumeration oracle.
      return 'auth:errors.invalidCredentials'
    case 403:
      return 'auth:errors.accountDisabled'
    case 429:
      return 'auth:errors.tooManyAttempts'
    default:
      return 'auth:errors.unexpected'
  }
}

/**
 * Best-effort identity from the access token itself, used when /auth/me is not
 * reachable. Nothing is authorized on it — the API re-derives roles and tenant
 * from the signature on every call — it only decides what the shell renders.
 */
function userFromAccessToken(token: string): AuthenticatedUser | null {
  const claims = decodeAccessTokenClaims(token)
  if (!claims) return null

  const extra = claims as unknown as { email?: string; name?: string; locale?: string }
  const locale = extra.locale && isSupportedLocale(extra.locale) ? extra.locale : DEFAULT_LOCALE

  return {
    id: claims.sub,
    tenantId: claims.tid ?? null,
    email: extra.email ?? '',
    displayName: extra.name ?? extra.email ?? claims.sub,
    roles: claims.roles,
    locale,
    sessionId: claims.sid,
  }
}

async function loadCurrentUser(): Promise<AuthenticatedUser | null> {
  try {
    return await api.get<AuthenticatedUser>(endpoints.me, { skipAuthRefresh: true })
  } catch {
    const token = getAccessToken()
    return token ? userFromAccessToken(token) : null
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'restoring',
  user: null,
  errorKey: null,
  isSubmitting: false,

  clearError: () => set({ errorKey: null }),

  login: async (credentials) => {
    set({ isSubmitting: true, errorKey: null })
    try {
      const response = await api.post<LoginResponse>(endpoints.login, credentials, {
        // A 401 here means "wrong password", not "stale access token": retrying it
        // through the refresh path would hide the real answer behind a rotation.
        skipAuthRefresh: true,
        credentials: 'include',
      })

      setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken })
      const user = response.user ?? userFromAccessToken(response.accessToken)
      set({ status: 'authenticated', user, isSubmitting: false, errorKey: null })

      applyUserLocale(user?.locale)
      return true
    } catch (error) {
      clearTokens()
      set({ status: 'anonymous', user: null, isSubmitting: false, errorKey: loginErrorKey(error) })
      return false
    }
  },

  logout: async () => {
    const refreshToken = getRefreshToken()
    try {
      // Fire-and-forget by design: the local session must end even if the server
      // is unreachable, otherwise a network fault leaves the user apparently
      // signed in on a shared workshop tablet.
      await api.post(endpoints.logout, { refreshToken }, { skipAuthRefresh: true })
    } catch {
      /* Ignored on purpose — see above. */
    }
    await forgetPlatformSession()
    notifySessionEnded('logout')
  },

  bootstrap: async () => {
    if (!getRefreshToken()) {
      set({ status: 'anonymous', user: null })
      return
    }

    set({ status: 'restoring' })
    const token = await refreshAccessToken()
    if (!token) {
      clearTokens()
      set({ status: 'anonymous', user: null })
      return
    }

    const user = await loadCurrentUser()
    if (!user) {
      clearTokens()
      set({ status: 'anonymous', user: null })
      return
    }

    set({ status: 'authenticated', user })
    applyUserLocale(user.locale)
  },
}))

// A revoked or expired session can be discovered by any in-flight request, deep
// inside the query layer. Rather than thread that back through every caller, the
// transport announces it once and the store reacts.
onSessionEnded((reason) => {
  useAuthStore.setState({
    status: 'anonymous',
    user: null,
    errorKey: reason === 'logout' ? null : 'auth:errors.sessionExpired',
  })
})
